"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCustomizeTableColumns = void 0;
const react_1 = require("react");
const ramda_1 = require("ramda");
const models_1 = require("./models");
const table_columns_1 = require("../../common/table-columns");
const utils_1 = require("./utils");
const feature_flags_1 = require("../../../feature-flags");
const hooks_1 = require("../../../hooks");
/**
 * Columns management (add, remove, reorder)
 */
const useCustomizeTableColumns = ({ initialColumns = table_columns_1.BASE_COLUMNS, options: { persisted = true, persistedTableData, setPersistedTableData, } = {}, } = {}) => {
    const { featureFlagsService } = (0, hooks_1.useFeatureFlagsService)();
    const initialValues = (0, react_1.useMemo)(() => {
        const options = [models_1.DynamicColumnsFilterOption.Original, models_1.DynamicColumnsFilterOption.Enriched];
        if (featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.Measures)) {
            options.push(models_1.DynamicColumnsFilterOption.Measures);
        }
        return {
            excludedColumns: initialColumns,
            columnsOrder: initialColumns,
            filterOptions: options,
        };
    }, [featureFlagsService, initialColumns]);
    const [localState, setLocalState] = (0, react_1.useState)(Object.assign({ columnsOrder: [], excludedColumns: [], selectAllMode: false, filterOptions: [] }, initialValues));
    const data = persisted ? persistedTableData : localState;
    const { columnsOrder, excludedColumns, selectAllMode, filterOptions, } = data;
    const updateGlobalState = setPersistedTableData;
    const updateLocalState = (0, react_1.useCallback)((newData) => setLocalState((prevState) => (Object.assign(Object.assign({}, prevState), newData))), []);
    const updateDynamicTableColumns = persisted ? updateGlobalState : updateLocalState;
    const onChangeColumns = (0, react_1.useCallback)((newData) => {
        updateDynamicTableColumns(newData);
    }, [updateDynamicTableColumns]);
    (0, react_1.useEffect)(() => {
        var _a, _b, _c;
        if (persisted) {
            const { selectAllMode: persistedSelectAllMode, columnsOrder: persistedColumnsOrder, excludedColumns: persistedExcludedColumns, filterOptions: persistedFilterOptions, } = persistedTableData;
            const updatedData = {};
            if (!persistedSelectAllMode && (0, ramda_1.isEmpty)(persistedExcludedColumns)) {
                updatedData.excludedColumns = (_a = initialValues.excludedColumns) !== null && _a !== void 0 ? _a : [];
            }
            if ((0, ramda_1.isEmpty)(persistedColumnsOrder)) {
                updatedData.columnsOrder = (_b = initialValues.columnsOrder) !== null && _b !== void 0 ? _b : [];
            }
            if ((0, ramda_1.isEmpty)(persistedFilterOptions)) {
                updatedData.filterOptions = (_c = initialValues.filterOptions) !== null && _c !== void 0 ? _c : [];
            }
            if (Object.keys(updatedData).length > 0) {
                updateDynamicTableColumns(updatedData);
            }
        }
    }, [persisted, initialValues, updateDynamicTableColumns, persistedTableData]);
    const toggleSelectAll = (0, react_1.useCallback)(() => updateDynamicTableColumns({
        selectAllMode: !selectAllMode,
        excludedColumns: [],
    }), [updateDynamicTableColumns, selectAllMode]);
    const onCheck = (0, react_1.useCallback)((columnId, isChecked) => {
        const newExcludedColumns = (0, utils_1.toggleSelectColumn)(columnId, isChecked, { excludedColumns, selectAllMode });
        updateDynamicTableColumns({ excludedColumns: newExcludedColumns });
    }, [excludedColumns, selectAllMode, updateDynamicTableColumns]);
    const onChangeColumnsOrder = (0, react_1.useCallback)((newColumnsOrder) => {
        updateDynamicTableColumns({ columnsOrder: newColumnsOrder });
    }, [updateDynamicTableColumns]);
    const checkIsColumnSelected = (0, react_1.useCallback)((columnId) => (0, utils_1.checkIsColumnSelected)(columnId, { excludedColumns, selectAllMode }), [selectAllMode, excludedColumns]);
    const isAllSelected = (0, react_1.useMemo)(() => selectAllMode && !excludedColumns.length, [excludedColumns.length, selectAllMode]);
    const [excludedLabelColumns, excludedMeasureColumns] = (0, react_1.useMemo)(() => {
        const { labels, measures } = excludedColumns.reduce((acc, columnId) => {
            var _a;
            const { label, dataMeta: { isMeasure } } = (_a = (0, table_columns_1.parseColumnId)(columnId)) !== null && _a !== void 0 ? _a : {};
            acc[isMeasure ? 'measures' : 'labels'].push(label);
            return acc;
        }, { labels: [], measures: [] });
        return [labels, measures];
    }, [excludedColumns]);
    return {
        columnsOrder,
        filterOptions,
        selectAllMode,
        excludedColumns,
        excludedLabelColumns,
        excludedMeasureColumns,
        isAllSelected,
        reviewAttribute: data === null || data === void 0 ? void 0 : data.reviewAttribute,
        checkIsColumnSelected,
        actions: {
            onCheck,
            onChangeColumns,
            toggleSelectAll,
            setOrder: onChangeColumnsOrder,
        },
    };
};
exports.useCustomizeTableColumns = useCustomizeTableColumns;
